import { ProfileShareSection } from '@eventr/common'
import {
    ColoursObj,
    FormDatePicker,
    FormInputCountrySelectorV2,
    FormInputText,
    FormSelectInput,
    listEntry,
    Typography,
} from '@eventr/common-storybook'
import { Stack, Switch } from '@mui/material'
import { Control, Controller } from 'react-hook-form'
import { useShallow } from 'zustand/react/shallow'
import i18n from '../../../i18n/i18next'
import { bs, useBoundStore } from '../../../state/bound.store'

interface ComponentFinderProps {
    sections: ProfileShareSection[]
    control?: any
    orgName?: string
    hiddenDetails?: boolean
}

export default function ProfileShareSectionFinder({
    sections,
    control,
    orgName,
    hiddenDetails = false,
}: ComponentFinderProps) {
    const { profileRequired } = useBoundStore(
        useShallow((s) => {
            return {
                profileRequired: s.profileRequired,
            }
        })
    )

    return (
        <Stack>
            {sections?.map((section) => (
                <Stack
                    key={section.id}
                    style={{ display: 'flex', paddingBottom: '16px' }}
                >
                    <Stack
                        flexDirection="row"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Stack
                            gap="5px"
                            flexDirection="row"
                            alignItems="baseline"
                        >
                            <Typography
                                sx={{ paddingBottom: '16px' }}
                                variant="bodyBold"
                            >
                                {i18n.t(
                                    `account.profile.additional-info.${section.id}.name`
                                )}
                            </Typography>
                            <Typography
                                sx={{ paddingBottom: '16px' }}
                                variant="bodySubtext"
                                color={ColoursObj.states.error}
                            >
                                {profileRequired?.includes(section.id) &&
                                    section.required
                                    ? `${i18n.t(`account.profile.required`)} ${orgName
                                        ? orgName
                                        : i18n.t(`account.profile.team`)
                                    }`
                                    : ''}
                            </Typography>
                        </Stack>
                        {!hiddenDetails && (
                            <FormInputSwitch
                                control={control as any}
                                sectionId={section.id}
                                name="sharedProperties"
                            />
                        )}
                    </Stack>
                    <Stack sx={{ gap: '16px' }}>
                        {ComponentFinder(control, section)}
                    </Stack>
                </Stack>
            ))}
        </Stack>
    )
}

function ComponentFinder(control: any, section: ProfileShareSection) {
    return section.fields?.map((field) => {
        const zodName = `additionalInfo.section_${section.id}.field_${field.id}`

        const fieldLabel = i18n.t(
            `account.profile.additional-info.${section.id}.fields.${field.id}`
        )
        const list = field?.listType
            ? bs().fetchList(field?.listType ?? '')
            : ''

        const listElements: listEntry[] = list
            ? list?.elements?.map((e) => ({
                id: e.id,
                displayName: e.name,
            })) ?? []
            : []

        switch (field.type) {
            case 'text':
                return (
                    <Stack key={field.id}>
                        <FormInputText
                            fieldLabel={fieldLabel}
                            name={zodName}
                            control={control as any}
                        />
                    </Stack>
                )
            case 'multiLineText':
                return (
                    <Stack key={field.id}>
                        <FormInputText
                            fieldLabel={fieldLabel}
                            name={zodName}
                            control={control as any}
                            multiline
                            rows={3}
                        />
                    </Stack>
                )
            case 'list':
                return (
                    <Stack key={field.id}>
                        <FormSelectInput
                            fieldLabel={fieldLabel}
                            name={zodName}
                            noneSelect="None"
                            data={listElements ?? []}
                            multiple
                            control={control as any}
                        />
                    </Stack>
                )
            case 'countrySelect':
                return (
                    <Stack key={field.id}>
                        <FormInputCountrySelectorV2
                            name={zodName}
                            fieldLabel={fieldLabel}
                            placeholder={i18n.t(
                                'edit-itinerary.fields.country.placeholder'
                            )}
                            control={control as any}
                        />
                    </Stack>
                )
            case 'dateSelect':
                return (
                    <Stack key={field.id}>
                        <FormDatePicker
                            fieldLabel={fieldLabel}
                            name={zodName}
                            control={control as any}
                        />
                    </Stack>
                )
            default:
                return null
        }
    })
}

type SharedSectionSwitchProps = {
    control: Control
    name: string
    sectionId: string
}

const FormInputSwitch: React.FC<SharedSectionSwitchProps> = ({
    control,
    name,
    sectionId,
}) => {
    return (
        <Controller
            name={name}
            control={control}
            render={({ field: { onChange, value }, fieldState }) => {
                const sharedArray = value ?? []
                const isChecked = sharedArray?.includes(sectionId) || false

                const handleToggle = () => {
                    if (isChecked) {
                        const updated = sharedArray.filter(
                            (item: string) => item !== sectionId
                        )
                        onChange(updated)
                    } else if (!sharedArray.includes(sectionId)) {
                        const updated = [...sharedArray, sectionId]
                        onChange(updated)
                    }
                }
                return (
                    <Switch
                        onChange={handleToggle}
                        checked={isChecked}
                        color={fieldState.error ? 'error' : 'primary'}
                    />
                )
            }}
        />
    )
}
